import { getImage } from "@/helpers/utils";

export type CreativeMediaType = "image" | "audio" | "video";

type CreativeMediaFields = {
  attachment?: string | null;
  channelName?: string | null;
  creativeType?: string | null;
};

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif"]);
const AUDIO_EXTENSIONS = new Set(["mp3"]);
const VIDEO_EXTENSIONS = new Set(["mp4", "mov"]);
const SUPPORTED_CREATIVE_TYPES = new Set<CreativeMediaType>([
  "image",
  "audio",
  "video",
]);

function getFileExtension(attachment?: string | null): string {
  if (!attachment) {
    return "";
  }

  const cleanPath = attachment.split(/[?#]/)[0] || "";
  const fileName = cleanPath.split("/").pop() || "";
  const extension = fileName.includes(".") ? fileName.split(".").pop() : "";

  return extension?.toLowerCase() || "";
}

export function getCreativeMediaType({
  attachment,
  channelName,
  creativeType,
}: CreativeMediaFields): CreativeMediaType {
  const normalizedCreativeType = String(creativeType || "")
    .trim()
    .toLowerCase();

  if (
    SUPPORTED_CREATIVE_TYPES.has(normalizedCreativeType as CreativeMediaType)
  ) {
    return normalizedCreativeType as CreativeMediaType;
  }

  const extension = getFileExtension(attachment);

  if (IMAGE_EXTENSIONS.has(extension)) {
    return "image";
  }

  if (AUDIO_EXTENSIONS.has(extension)) {
    return "audio";
  }

  if (VIDEO_EXTENSIONS.has(extension)) {
    return "video";
  }

  const normalizedChannelName = String(channelName || "")
    .trim()
    .toLowerCase();

  if (normalizedChannelName === "audio") {
    return "audio";
  }

  if (normalizedChannelName === "video") {
    return "video";
  }

  return "image";
}

export function getCreativeMediaSourceType(
  attachment?: string | null
): string | undefined {
  const extension = getFileExtension(attachment);

  if (extension === "mp3") {
    return "audio/mpeg";
  }

  if (extension === "mp4") {
    return "video/mp4";
  }

  return undefined;
}

export function getCreativeMediaUrl(attachment?: string | null): string {
  if (!attachment) {
    return "";
  }

  if (/^https?:\/\//i.test(attachment)) {
    return attachment;
  }

  return getImage(attachment, "CREATIVE");
}
